"use client";

import { motion } from "framer-motion";
import { Card, Chip } from "@heroui/react";
import { BreachesSection } from "./breaches-section";
import { BREACHES_EXPLAINERS } from "./breaches-data";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06 },
  }),
};

// Small result preview shown at the top of the Password breaches card.
function PasswordMockup() {
  return (
    <Card
      variant="default"
      className="flex h-41 flex-col justify-between rounded-xl border-0! bg-white! p-3 dark:bg-surface-base!"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-sm tracking-[0.3em] text-text-tertiary">••••••••</span>
        <Chip size="sm" color="danger" variant="soft" className="text-4xs">Found · 1,203×</Chip>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col">
          <span className="text-4xs uppercase tracking-wider text-text-quaternary">Method</span>
          <span className="text-xs font-medium text-text-secondary">k-anonymity</span>
        </div>
        <div className="flex flex-col">
          <span className="text-4xs uppercase tracking-wider text-text-quaternary">Sent upstream</span>
          <span className="text-xs font-medium text-text-secondary">5 chars</span>
        </div>
      </div>
      <p className="text-4xs text-text-quaternary">Your password never leaves the browser.</p>
    </Card>
  );
}

function MockupShell({ children }: { children: React.ReactNode }) {
  return (
    <Card
      variant="default"
      className="flex h-41 flex-col justify-between rounded-xl border-0! bg-white! p-3 dark:bg-surface-base!"
    >
      {children}
    </Card>
  );
}

function MiniKV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-4xs uppercase tracking-wider text-text-quaternary">{k}</span>
      <span className="text-xs font-medium text-text-secondary">{v}</span>
    </div>
  );
}

function GithubMockup() {
  return (
    <MockupShell>
      <div className="flex items-center justify-between">
        <span className="text-4xs uppercase tracking-wider text-text-quaternary">Repo scan</span>
        <Chip size="sm" color="danger" variant="soft" className="text-4xs">3 secrets found</Chip>
      </div>
      <div className="space-y-1 font-mono text-4xs leading-relaxed">
        <p className="text-text-tertiary"><span className="text-text-quaternary">config/prod.env:12</span> · sk-…AB12</p>
        <p className="text-text-tertiary"><span className="text-text-quaternary">src/aws.ts:88</span> · AKIA…7Q</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MiniKV k="Scope" v="Tree + history" />
        <MiniKV k="Commit" v="a1b2c3d" />
      </div>
    </MockupShell>
  );
}

function ExposedKeysMockup() {
  return (
    <MockupShell>
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-text-tertiary">sk-…9f4c</span>
        <Chip size="sm" color="danger" variant="soft" className="text-4xs">Live · exposed</Chip>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <MiniKV k="Provider" v="OpenAI" />
        <MiniKV k="Source" v="Public gist" />
      </div>
      <p className="text-4xs text-text-quaternary">Matched to a domain you own.</p>
    </MockupShell>
  );
}

const MOCKUPS = {
  password: PasswordMockup,
  github: GithubMockup,
  exposed: ExposedKeysMockup,
} as const;

export function BreachesExplainers() {
  return (
    <BreachesSection>
      <section className="heroui pb-40">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h2 className="mb-3 text-2xl font-semibold tracking-tight sm:text-3xl">
            Every breach lookup, explained
          </h2>
          <p className="mx-auto max-w-lg text-sm text-text-tertiary sm:text-base">
            Find leaked secrets before someone else does.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BREACHES_EXPLAINERS.map((l, i) => {
            const Mockup = l.mockup ? MOCKUPS[l.mockup] : null;
            const inner = (
              <>
                {Mockup && <Mockup />}
                <div className={l.mockup ? "mt-5" : ""}>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-base font-semibold text-text-primary">{l.title}</h3>
                    {l.tag && (
                      <Chip size="sm" variant="soft" className="shrink-0 border-0! bg-brand/10! text-4xs tracking-wider text-brand! uppercase">
                        {l.tag}
                      </Chip>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-2 min-h-10 text-sm leading-snug text-text-tertiary">
                    {l.desc}
                  </p>
                </div>
              </>
            );
            return (
              <motion.div
                key={l.title}
                // Anchor target so the footer can deep-link to a single check.
                id={l.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}
                className="scroll-mt-24"
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <Card
                  variant="default"
                  className="group flex h-full flex-col rounded-[20px] border-0! bg-sidebar! p-6 text-left transition-colors dark:bg-card!"
                  {...(l.href
                    ? {
                        render: (props: React.ComponentProps<"div">) => (
                          <a
                            {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
                            href={l.href}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }
                    : {})}
                >
                  {inner}
                </Card>
              </motion.div>
            );
          })}
        </div>
      </section>
    </BreachesSection>
  );
}
